'use client';

import { assignInlineVars } from '@vanilla-extract/dynamic';
import {
	anchorName,
	arrowDown,
	arrowUp,
	innerAnchorName,
	flyout,
	flyoutAnchor,
	flyoutPopover,
	headerMetaSharesheetButton,
} from './sharesheet.css';
import {
	Dispatch,
	ReactNode,
	SetStateAction,
	useCallback,
	useId,
	useRef,
	useState,
} from 'react';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { secondaryButtonBox } from '@/uiComposites/interactive/callToAction';
import { box } from '@/uiPrimitives/layout/box';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { icon, IconProps } from '@/uiPrimitives/media/icon';
import { shareIcon } from '@/uiIcons/shareIcon.css';
import { stack } from '@/uiPrimitives/layout/stack';
import { IconLinkContent } from '@/uiComposites/interactive/IconLink';
import { emailIcon } from '@/uiIcons/emailIcon.css';
import { translateInline } from '@/globals/translate/translateInline';
import { placement } from '@/uiPrimitives/layout/placement.css';
import { copyLinkIcon } from '@/uiIcons/copyLinkIcon.css';
import { onInteractOutsideOfRef } from '@/globals/helpers/useInteractOutside';
import { position } from '@/uiPrimitives/layout/position';
import {
	sharesheetButtonTranslations,
	sharesheetStatusTranslations,
} from './sharesheetTranslations';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { unprintable } from '@/uiPrimitives/base/printable.css';
import { printerIcon } from '@/uiIcons/printerIcon.css';
import {
	hiddenAccessible,
	hiddenAccessibleBelowMQ640px,
} from '@/uiPrimitives/utility/display.css';
import { checkIcon } from '@/uiIcons/checkIcon.css';
import { warningIcon } from '@/uiIcons/warningIcon.css';

const Flyout = ({
	id,
	buttonSlot,
	pageTitle,
	setShareState,
}: {
	id: string;
	buttonSlot: ReactNode;
	pageTitle: string;
	setShareState: Dispatch<SetStateAction<ShareSheetStatus>>;
}) => {
	const flyoutRef = useRef<HTMLDivElement>(null);
	const closeFlyout = useCallback(() => {
		if (flyoutRef.current == null) return;
		flyoutRef.current.hidePopover();
	}, []);

	return (
		<div
			style={{
				...assignInlineVars({
					[anchorName]: `--flyout-${id}`,
					[innerAnchorName]: `--flyout-inner-${id}`,
				}),
			}}
			className={classes(
				box({ inlineSize: 'fit-content' }),
				position({ position: 'relative' }),
			)}
		>
			{buttonSlot}
			<div
				className={classes(flyoutPopover, textStyle({ capSize: '0 | p' }))}
				id={id}
				popover={'auto'}
				ref={flyoutRef}
			>
				<div
					tabIndex={-1}
					className={classes(
						flyout,
						box({ padding: '1 | h3', borderRadius: '4px' }),
					)}
				>
					<ul
						className={classes(
							stack({
								rowGap: '3 | h1',
							}),
						)}
					>
						<li>
							<button
								className={classes(box({}))}
								onClick={async () => {
									closeFlyout();
									setShareState('idle');

									try {
										await navigator.clipboard.writeText(window.location.href);
										setShareState('copySuccess');
									} catch (_error) {
										setShareState('copyError');
									}
								}}
							>
								<IconLinkContent
									icon={
										<i
											className={classes(
												icon({
													icon: copyLinkIcon,
													blockSize: '2 | h2',
												}),
												placement({ justifySelf: 'center' }),
											)}
										/>
									}
								>
									{translateInline({
										en: 'Copy link',
										nl: 'Kopieer link',
									})}
								</IconLinkContent>
							</button>
						</li>
						<li>
							<button
								onClick={() => {
									closeFlyout();
									setShareState('emailTried');

									window.location.href = `mailto:?subject=${encodeURIComponent(
										pageTitle,
									)}&body=${encodeURIComponent(`${pageTitle}: ${window.location.href}`)}`;
								}}
								className={classes(box({}))}
							>
								<IconLinkContent
									icon={
										<i
											className={classes(
												icon({
													icon: emailIcon,
													blockSize: '0 | p',
												}),
												placement({ justifySelf: 'center' }),
											)}
										/>
									}
								>
									{translateInline({
										en: 'Share (email)',
										nl: 'Deel via e-mail',
									})}
								</IconLinkContent>
							</button>
						</li>
						<li>
							<button
								onClick={() => {
									closeFlyout();
									setShareState('idle');

									window.print();
								}}
								className={classes(box({}))}
							>
								<IconLinkContent
									icon={
										<i
											className={classes(
												icon({
													icon: printerIcon,
													blockSize: '1 | h3',
												}),
												placement({ justifySelf: 'center' }),
											)}
										/>
									}
								>
									{translateInline({
										en: 'Print',
										nl: 'Printen',
									})}
								</IconLinkContent>
							</button>
						</li>
						<li>
							<button
								className={classes(box({}))}
								onClick={async () => {
									closeFlyout();
									setShareState('idle');
									if (await openNativeSharesheet()) {
										setShareState('idle');
									} else {
										setShareState('shareSheetError');
									}
								}}
							>
								<IconLinkContent
									icon={
										<i
											className={classes(
												icon({
													icon: shareIcon,
													blockSize: '1 | h3',
												}),
												placement({ justifySelf: 'center' }),
											)}
										/>
									}
								>
									{translateInline({
										en: 'Share (other)',
										nl: 'Meer deelopties',
									})}
								</IconLinkContent>
							</button>
						</li>
					</ul>
				</div>
				<i className={classes(arrowUp)}></i>
				<i className={classes(arrowDown)}></i>
			</div>
		</div>
	);
};

const isMobile = () => {
	if (!('userAgentData' in navigator)) return false;

	// @ts-expect-error - userAgentData is a new DOM api
	return navigator.userAgentData.mobile;
};

// There is a bug in iOS Safari popovers before version 16 and 26+
// that makes them unusable. Therefore we disable the popover sharesheet
// for these versions, until a fix is in place.
// See: https://bugs.webkit.org/show_bug.cgi?id=285811 (although it is marked as fixed here, it should be opened again for iOS 26+)
const isSafariUnstablePopoverVersion = () => {
	if (typeof navigator === 'undefined') return false;
	const ua = navigator.userAgent;
	// Check for Safari (but not Chrome or Edge, which also include 'Safari' in UA)
	const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR/.test(ua);
	if (!isSafari) return false;
	const match = ua.match(/Version\/(\d+)/i);
	if (!match) return false;
	const version = parseInt(match[1], 10);
	return version <= 16 || version >= 26;
};

const shouldUsePopupSharesheet = () => {
	return (
		!isMobile() &&
		Object.hasOwn(HTMLElement.prototype, 'popover') &&
		!isSafariUnstablePopoverVersion()
	);
};

const ShareButton = ({
	id,
	shareState,
	setShareState,
	buttonLabel,
	className,
	iconSize,
}: {
	id: string;
	shareState: ShareSheetStatus;
	setShareState: Dispatch<SetStateAction<ShareSheetStatus>>;
	buttonLabel: ReactNode;
	className?: string;
	iconSize?: IconProps['blockSize'];
}) => {
	let statusText = '';
	switch (shareState) {
		case 'copySuccess':
			statusText = sharesheetStatusTranslations['Link copied'];
			break;
		case 'emailTried':
			statusText =
				sharesheetStatusTranslations['We’re trying to open your email program'];
			break;
		case 'copyError':
			statusText = sharesheetStatusTranslations['Cannot copy link'];
			break;
		case 'shareSheetError':
			statusText = sharesheetStatusTranslations['Cannot open share options'];
			break;
		default:
			break;
	}
	const isSuccessfulState =
		shareState === 'copySuccess' || shareState === 'emailTried';

	return (
		<button
			popoverTarget={id}
			onClick={async (event) => {
				setShareState('idle');
				if (shouldUsePopupSharesheet()) return;

				event.preventDefault();
				if (await openNativeSharesheet()) {
					setShareState('idle');
				} else {
					setShareState('shareSheetError');
				}
			}}
			className={className}
			{...addTestSelector('uiSharesheetButton')}
		>
			<span
				className={classes(
					occupy({ blockSize: 'capSize', inlineSize: iconSize }),
				)}
			>
				<i
					className={classes(
						flyoutAnchor,
						shareState !== 'idle' && hiddenAccessible,
						icon({
							icon: shareIcon,
							blockSize: iconSize,
						}),
					)}
				></i>
				<i
					role="status"
					title={statusText}
					tabIndex={-1}
					ref={onInteractOutsideOfRef(() => {
						setShareState('idle');
					})}
					className={classes(
						shareState === 'idle' && hiddenAccessible,
						icon({
							icon: isSuccessfulState ? checkIcon : warningIcon,
							fill: isSuccessfulState ? 'positiveGreen' : 'positiveOrange',
							blockSize: iconSize,
						}),
					)}
				></i>
			</span>
			<span className={classes(textBlock({}))}>{buttonLabel}</span>
		</button>
	);
};

type ShareSheetStatus =
	| 'idle'
	| 'copyError'
	| 'copySuccess'
	| 'shareSheetError'
	| 'emailTried';

export const Sharesheet = ({
	buttonLabel,
	pageTitle,
	className,
	buttonClassName = classes(
		secondaryButtonBox({}),
		shelf({ columnGap: '1 | h3' }),
	),
	buttonIconSize = '1 | h3',
}: {
	buttonLabel: ReactNode;
	pageTitle: string;
	className?: string;
	buttonClassName?: string;
	buttonIconSize?: IconProps['blockSize'];
}) => {
	const id = useId();
	// Sharesheet success does not have it's own state
	// because the native sharesheet appearing is enough feedback
	// Email sucess or error is not handled either,
	// because the user is redirected to their email client.
	const [shareState, setShareState] = useState<ShareSheetStatus>('idle');

	return (
		<div
			className={classes(
				box({ inlineSize: 'fit-content' }),
				unprintable,
				className,
			)}
		>
			<Flyout
				id={id}
				setShareState={setShareState}
				pageTitle={pageTitle}
				buttonSlot={
					<ShareButton
						id={id}
						shareState={shareState}
						setShareState={setShareState}
						buttonLabel={buttonLabel}
						className={buttonClassName}
						iconSize={buttonIconSize}
					/>
				}
			/>
		</div>
	);
};

export const HeaderMetaSharesheet = ({ pageTitle }: { pageTitle: string }) => {
	return (
		<Sharesheet
			pageTitle={pageTitle}
			buttonLabel={
				<span className={hiddenAccessibleBelowMQ640px}>
					{sharesheetButtonTranslations['Share']}
				</span>
			}
			buttonClassName={classes(
				shelf({ gap: { min640px: '-2 | smaller', max640px: '0px' } }),
				textStyle({ color: 'positiveBlue' }),
				headerMetaSharesheetButton,
			)}
			buttonIconSize={{ max640px: '2 | h2', min640px: '1 | h3' }}
		/>
	);
};

async function openNativeSharesheet() {
	if (!('share' in navigator)) return false;
	try {
		await navigator.share({
			url: window.location.href,
		});
		return true;
	} catch (error) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			return true;
		}
		return false;
	}
}
